import type {
  CapabilityCatalogs,
  CapabilityChip,
  CapabilitySelection,
  SelectOption,
  SelectOptionGroup,
  SlugData,
  Technology,
} from '@indutrust/shared-model';

export type CatalogLeafMatch = {
  leaf: SlugData;
  parent: Technology;
};

/** Capability chips grouped under a Technology parent name. */
export type CapabilityChipGroup = {
  parentName: string;
  chips: CapabilityChip[];
};

/** Maps capability leaves to flat select options. */
export function mapTechnologyLeavesToSelectOptions(
  technologies: readonly Technology[]
): SelectOption[] {
  return technologies.flatMap((technology) =>
    technology.capabilities.map((leaf) => ({
      label: `${technology.name} — ${leaf.name}`,
      value: leaf.id,
    }))
  );
}

/** Maps capability leaves to parent-grouped select options. */
export function mapTechnologyLeavesToSelectOptionGroups(
  technologies: readonly Technology[]
): SelectOptionGroup[] {
  return technologies.map((technology) => ({
    label: technology.name,
    value: technology.id,
    options: technology.capabilities.map((leaf) => ({
      label: leaf.name,
      value: leaf.id,
    })),
  }));
}

/** Finds a capability leaf under the technology catalog. */
export function findCatalogLeafMatch(
  leafId: number,
  technologies: readonly Technology[]
): CatalogLeafMatch | undefined {
  for (const parent of technologies) {
    const leaf = parent.capabilities.find((entry) => entry.id === leafId);
    if (leaf) {
      return { leaf, parent };
    }
  }
  return undefined;
}

/** Resolves structured capability chips from selected leaf ids. */
export function resolveCapabilityChips(
  selection: CapabilitySelection,
  catalogs: CapabilityCatalogs
): CapabilityChip[] {
  const chips: CapabilityChip[] = [];

  for (const id of selection.capability_ids) {
    const match = findCatalogLeafMatch(id, catalogs.technologies);
    if (match) {
      chips.push({
        id,
        leafName: match.leaf.name,
        parentName: match.parent.name,
      });
    }
  }

  return chips;
}

/** Resolves human-readable capability labels from selected leaf ids. */
export function resolveCapabilityLabels(
  selection: CapabilitySelection,
  catalogs: CapabilityCatalogs
): string[] {
  return resolveCapabilityChips(selection, catalogs).map(
    (chip) => `${chip.parentName} — ${chip.leafName}`
  );
}

/**
 * Groups capability chips by Technology parent name, preserving first-seen order.
 */
export function groupCapabilityChipsByParent(
  chips: readonly CapabilityChip[]
): CapabilityChipGroup[] {
  const groups = new Map<string, CapabilityChip[]>();

  for (const chip of chips) {
    const existing = groups.get(chip.parentName) ?? [];
    existing.push(chip);
    groups.set(chip.parentName, existing);
  }

  return [...groups.entries()].map(([parentName, groupChips]) => ({
    parentName,
    chips: groupChips,
  }));
}

/** Normalizes a partial capability selection into a full selection. */
export function normalizeCapabilitySelection(
  selection?: Partial<CapabilitySelection> | null
): CapabilitySelection {
  return {
    capability_ids: [...(selection?.capability_ids ?? [])],
  };
}

/** Merges multiple capability selections into a deduplicated union. */
export function mergeCapabilitySelections(
  ...selections: readonly (Partial<CapabilitySelection> | null | undefined)[]
): CapabilitySelection {
  const capabilityIds = new Set<number>();

  for (const selection of selections) {
    const normalized = normalizeCapabilitySelection(selection);
    for (const id of normalized.capability_ids) {
      capabilityIds.add(id);
    }
  }

  return {
    capability_ids: [...capabilityIds],
  };
}
